import { useState } from "react";
import { getShopFromUrl } from "../utils/shop";
import "./Login.css";

function Field({ icon, label, value, onChange, type = "text", placeholder }) {
  return (
    <label className="login-field">
      <span className="login-field__icon" aria-hidden="true">
        {icon}
      </span>
      <span className="login-field__body">
        <span className="login-field__label">{label}</span>
        <input
          className="login-field__input"
          type={type}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          placeholder={placeholder}
        />
      </span>
    </label>
  );
}

function ShieldIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 3 6 5.5v5.3c0 4.1 2.7 7.8 6 9.2 3.3-1.4 6-5.1 6-9.2V5.5L12 3Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M8.5 12.3 11 14.8l4.8-5.1" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function KeyIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M15.5 10.5a4.5 4.5 0 1 0-1.2 3.1l4.7 4.7h2v-2h-1.4l-1.5-1.5h-1.9l-1.2-1.2h-1.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="10.5" cy="10.5" r="1.3" fill="currentColor" />
    </svg>
  );
}

function CubeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="m12 3 7 4v10l-7 4-7-4V7l7-4Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M12 3v14" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="m5 7 7 4 7-4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 12.2a4.3 4.3 0 1 0-4.3-4.3A4.3 4.3 0 0 0 12 12.2Z" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M5.5 20a6.5 6.5 0 0 1 13 0" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function GlobeIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="12" r="8.2" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M3.8 12h16.4M12 3.8c2.6 2.5 4 5.4 4 8.2s-1.4 5.7-4 8.2c-2.6-2.5-4-5.4-4-8.2s1.4-5.7 4-8.2Z" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

export default function Login({ onLoginSuccess }) {
  const [hasNetSuiteAccountId, setHasNetSuiteAccountId] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    authCode: "",
    licenseKey: "",
    productCode: "",
    accountId: "",
    licenceUrl: "",
  });

  const validateForm = () => {
    if (!form.authCode || !form.licenseKey || !form.productCode || !form.licenceUrl) {
      alert("Please fill all required fields");
      return false;
    }

    if (hasNetSuiteAccountId && !form.accountId) {
      alert("Please enter your NetSuite Account ID");
      return false;
    }

    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const endpoint = hasNetSuiteAccountId ? "/api/login/netsuite" : "/api/login/rental";
    const debugTraceId = `login-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const currentShop = getShopFromUrl();
    const payload = {
      debugTraceId,
      shop: currentShop,
      authCode: form.authCode,
      licenseKey: form.licenseKey,
      productCode: form.productCode,
      licenceUrl: form.licenceUrl,
      licenseUrl: form.licenceUrl,
    };

    if (hasNetSuiteAccountId) {
      payload.accountId = form.accountId;
    }

    console.group(`[Rental Login Debug] ${debugTraceId}`);
    console.log("Entered form values:", form);
    console.log("Has NetSuite Account ID:", hasNetSuiteAccountId);
    console.log("Selected endpoint:", endpoint);
    console.log("Payload posted to rental backend:", payload);

    try {
      setLoading(true);
      console.log("Sending fetch request to rental backend...");
      const res = await fetch(endpoint, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      console.log("Rental backend response status:", res.status);
      console.log("Rental backend response ok:", res.ok);
      console.log(
        "Rental backend code version header:",
        res.headers.get("X-Rental-Code-Version"),
      );

      if (!res.ok) {
        const errorText = await res.text();
        console.log("Raw rental backend error response:", errorText);
        console.error("Server Error:", res.status, errorText);
        let errorMessage = `Server Error (${res.status})`;
        try {
          const errorJson = JSON.parse(errorText);
          console.log("Parsed rental backend error response:", errorJson);
          console.error("Login debug details:", errorJson.debug || errorJson);
          errorMessage = errorJson.error || errorMessage;
        } catch (e) {
          console.warn("Server error response was not JSON");
        }
        alert(errorMessage);
        return;
      }

      const data = await res.json();
      console.log("Parsed rental backend success response:", data);
      if (data.success) {
        const licenseObj = data.user || data;
        console.log("License object saved to localStorage:", licenseObj);
        try {
          localStorage.setItem("license", JSON.stringify(licenseObj));
        } catch (e) {
          console.warn("Failed to save license", e);
        }
        onLoginSuccess?.(licenseObj);
        window.location.href = `/${window.location.search || ""}`;
      } else {
        alert(data.error || "Login failed");
      }
    } catch (err) {
      console.error("Login Failed:", err);
      alert("Unable to connect to server");
    } finally {
      setLoading(false);
      console.groupEnd();
    }
  };

  const fieldSet = [
    { key: "authCode", label: "Auth Code", placeholder: "Enter auth code", icon: <ShieldIcon /> },
    { key: "licenseKey", label: "License Key", placeholder: "Enter license key", icon: <KeyIcon /> },
    { key: "productCode", label: "Product Code", placeholder: "Enter product code", icon: <CubeIcon /> },
    { key: "licenceUrl", label: "Licence URL", placeholder: "Enter licence URL", icon: <GlobeIcon /> },
  ];

  return (
    <div className="login-page">
      <div className="login-page__shell">
        <aside className="login-brand-panel">
          <div className="login-brand-panel__dots" aria-hidden="true" />
          <div className="login-brand-panel__rings" aria-hidden="true" />
          <div className="login-brand-panel__content">
            <img
              className="login-brand-panel__logo"
              src="/netscore-white-logo.png"
              alt="NetScore Rental Management"
            />
            <div className="login-brand-panel__subtitle">Rental Management</div>
            <div className="login-brand-panel__divider" aria-hidden="true" />
          </div>
        </aside>

        <main className="login-form-panel">
          <div className="login-form-card">
            <div className="login-card__dots" aria-hidden="true" />
            <section className="login-heading-row">
              <div className="login-heading-row__copy">
                <h1>Activate Your Licence check</h1>
                <p>Fill the licence details</p>
              </div>
            </section>

            <div className="login-fields">
              {fieldSet.map((field) => (
                <Field
                  key={field.key}
                  icon={field.icon}
                  label={field.label}
                  value={form[field.key]}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, [field.key]: value }))
                  }
                  type={field.type || "text"}
                  placeholder={field.placeholder}
                />
              ))}

              <label className="login-checkbox">
                <input
                  type="checkbox"
                  checked={hasNetSuiteAccountId}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setHasNetSuiteAccountId(checked);
                    if (!checked) {
                      setForm((prev) => ({ ...prev, accountId: "" }));
                    }
                  }}
                />
                <span>I have NetSuite Account ID</span>
              </label>

              {hasNetSuiteAccountId ? (
                <Field
                  icon={<UserIcon />}
                  label="NetSuite Account ID"
                  value={form.accountId}
                  onChange={(value) =>
                    setForm((prev) => ({ ...prev, accountId: value }))
                  }
                  placeholder="Enter NetSuite account ID"
                />
              ) : null}
            </div>

            <button className="login-submit" type="button" onClick={handleSubmit} disabled={loading}>
              <span>{loading ? "Activating..." : "Activate"}</span>
            </button>

            <div className="login-footer-note">Your data is safe and secure</div>
          </div>
        </main>
      </div>
    </div>
  );
}
